import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';

type PlannerFieldType = 'text' | 'number' | 'currency';
type PlannerValueFormat = 'compact' | 'number';

interface PlannerSliderConfig {
  key: string;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  editable?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  decimals?: number;
  minLabel?: string;
  maxLabel?: string;
  helperText?: string;
  secondaryValue?: unknown;
  secondaryValueKey?: string;
  secondaryPrefix?: string;
  secondarySuffix?: string;
  secondaryDecimals?: number;
  valueFormat?: PlannerValueFormat;
  minMultiplier?: number;
  maxMultiplier?: number;
  requestKey?: string;
  requestValueKey?: string;
  displayValueKey?: string;
  displayValuePrefix?: string;
  displayValueSuffix?: string;
  displayValueFormat?: PlannerValueFormat;
  displayValueDecimals?: number;
  derivedValue?: {
    targetKey?: string;
    baseValueKey?: string;
    divisorKey?: string;
    mode?: 'decrease-by-percent' | 'increase-by-percent';
  };
  absoluteValue?: boolean;
}

interface PlannerFieldConfig {
  key: string;
  label: string;
  type?: PlannerFieldType;
  value?: unknown;
  defaultValue?: unknown;
  editable?: boolean;
  placeholder?: string;
  helperText?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  min?: number;
  max?: number;
  valueFormat?: PlannerValueFormat;
  requestKey?: string;
}

interface PlannerResultRowConfig {
  label: string;
  value?: unknown;
  valueKey?: string;
  template?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  tone?: 'neutral' | 'positive' | 'negative' | 'auto';
  positiveColor?: string;
  negativeColor?: string;
  neutralColor?: string;
  valueFormat?: PlannerValueFormat;
}

interface PlannerActionButtonConfig {
  label?: string;
  apiUrl?: string;
  method?: string;
  requestPayload?: any;
  queryParams?: Record<string, any>;
  successMessage?: string;
  errorMessage?: string;
  responseMessagePath?: string;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  backgroundColor?: string;
  textColor?: string;
  navigationValue?: string;
  functionName?: string;
  version?: string;
  usePlannerDataRequest?: boolean;
  includeNavigationRequestParamsOnSubmit?: boolean;
  submitRequestKeys?: string[];
  applyRequestKeys?: string[];
  refreshPlannerDataOnSuccess?: boolean;
  runNavigationOnSuccess?: boolean;
}

interface PricePlannerConfig {
  widgetId?: string;
  title?: string;
  subtitle?: string;
  subtitleTemplate?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  showClose?: boolean;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  sliders?: PlannerSliderConfig[];
  fields?: PlannerFieldConfig[];
  resultRows?: PlannerResultRowConfig[];
  actionButton?: PlannerActionButtonConfig;
  apiUrl?: string;
  method?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
  themeOverrides?: any;
  dataApiUrl?: string;
  dataMethod?: string;
  dataFunctionName?: string;
  dataVersion?: string;
  functionName?: string;
  version?: string;
  defaultRequestData?: Record<string, any>;
  dataRequestFields?: string[];
  dataResponseMappings?: {
    sliders?: Record<string, string>;
    fields?: Record<string, string>;
    resultRows?: Record<string, string>;
  };
}

@Component({
  selector: 'app-price-planner',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './price-planner.component.html',
  styleUrl: './price-planner.component.scss',
})
export class PricePlannerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data?: PricePlannerConfig | null;

  widgetConfig: PricePlannerConfig | null = null;
  sliderConfigs: PlannerSliderConfig[] = [];
  fieldConfigs: PlannerFieldConfig[] = [];
  resultRows: PlannerResultRowConfig[] = [];
  formState: Record<string, any> = {};
  dismissed = false;
  loading = false;
  submitting = false;
  errorMessage = '';
  submitMessage = '';
  submitErrorMessage = '';
  fontScale = 1;
  resolvedSubtitle = '';

  private readonly configService = inject(ConfigService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly themeService = inject(ThemeService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);
  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private configSub?: Subscription;
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private themeSub?: Subscription;

  @HostBinding('style.--price-planner-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  ngOnInit(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.fontScale = this.responsiveLayoutService.getFontScale();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.applyResolvedTheme();
    });

    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeComponent();
    }
  }

  ngOnDestroy(): void {
    this.configSub?.unsubscribe();
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
  }

  get showClose(): boolean {
    return this.widgetConfig?.showClose !== false;
  }

  get titleAlignment(): string {
    return this.widgetConfig?.titleAlignment || 'left';
  }

  get buttonLabel(): string {
    return this.widgetConfig?.actionButton?.label || 'Apply Changes';
  }

  onSliderChange(slider: PlannerSliderConfig, rawValue: unknown): void {
    if (!slider.key) {
      return;
    }

    const nextValue = this.clampNumber(
      this.toFiniteNumber(rawValue, this.formState[slider.key] ?? slider.value ?? slider.defaultValue ?? 0),
      this.toFiniteNumber(slider.min, 0),
      this.toFiniteNumber(slider.max, 100),
    );
    const decimals = this.decimalPlaces(slider.step ?? 1);
    this.formState[slider.key] = Number(nextValue.toFixed(decimals));
    this.updateDerivedSliderValue(slider);
    this.refreshPlannerData();
  }

  onFieldChange(field: PlannerFieldConfig, rawValue: unknown): void {
    if (!field.key || field.editable === false) {
      return;
    }

    if (field.type === 'number' || field.type === 'currency') {
      const numeric = this.toFiniteNumber(rawValue, this.formState[field.key]);
      const clamped = this.clampNumber(
        numeric,
        field.min === undefined ? Number.NEGATIVE_INFINITY : Number(field.min),
        field.max === undefined ? Number.POSITIVE_INFINITY : Number(field.max),
      );
      const decimals = Number(field.decimals ?? 0);
      this.formState[field.key] = Number.isFinite(clamped)
        ? Number(clamped.toFixed(decimals))
        : this.formState[field.key];
      this.refreshPlannerData();
      return;
    }

    this.formState[field.key] = rawValue ?? '';
    this.refreshPlannerData();
  }

  dismiss(): void {
    this.dismissed = true;
  }

  submitPlanner(): void {
    const actionButton = this.widgetConfig?.actionButton;
    if (this.submitting) {
      return;
    }

    this.submitMessage = '';
    this.submitErrorMessage = '';

    const resolvedState = this.getResolvedState();
    this.emitFilterPayload(resolvedState, actionButton);

    if (actionButton?.apiUrl) {
      const apiUrl = String(this.resolveDynamicValue(actionButton.apiUrl) || '');
      const method = this.resolveMethod(apiUrl, actionButton.method);
      const requestPayload = actionButton.usePlannerDataRequest
        ? this.buildPlannerDataRequest(
            actionButton.includeNavigationRequestParamsOnSubmit !== false,
            actionButton.submitRequestKeys || actionButton.applyRequestKeys,
          )
        : this.resolveDynamicValue(
            actionButton.requestPayload ?? {
              state: resolvedState,
              widgetId: this.widgetConfig?.widgetId,
            },
          );
      const queryParams = this.resolveDynamicValue(actionButton.queryParams);

      this.submitting = true;
      this.configService
        .getConfig(
          apiUrl,
          {
            ...actionButton,
            functionName: actionButton.functionName || this.widgetConfig?.dataFunctionName || this.widgetConfig?.functionName,
            version: actionButton.version || this.widgetConfig?.dataVersion || this.widgetConfig?.version || '1.0',
          },
          this.buildRequestFilters(requestPayload, queryParams),
          method,
        )
        .subscribe({
          next: (response) => {
            const responseMessagePath = String(this.resolveDynamicValue(actionButton.responseMessagePath || '') || '');
            const responseMessage = responseMessagePath ? this.getValueByPath(response, responseMessagePath) : '';
            this.submitting = false;
            this.submitMessage = String(responseMessage || actionButton.successMessage || 'Changes applied successfully.');
            this.submitErrorMessage = '';
            if (actionButton.refreshPlannerDataOnSuccess !== false) {
              this.refreshPlannerData();
            }
            if (actionButton.runNavigationOnSuccess === true) {
              this.runNavigationAction(actionButton);
            }
          },
          error: () => {
            this.submitting = false;
            this.submitMessage = '';
            this.submitErrorMessage = actionButton.errorMessage || 'Unable to apply changes.';
          },
        });
      return;
    }

    this.submitMessage = actionButton?.successMessage || 'Changes applied successfully.';
    this.runNavigationAction(actionButton);
  }

  getSliderDisplayValue(slider: PlannerSliderConfig): string {
    const resolvedState = this.getResolvedState();
    const value = slider.displayValueKey
      ? resolvedState[slider.displayValueKey]
      : resolvedState[slider.key];
    const primary = this.formatValue(
      value,
      slider.displayValueDecimals ?? slider.decimals ?? this.decimalPlaces(slider.step ?? 1),
      slider.displayValuePrefix ?? slider.valuePrefix,
      slider.displayValueSuffix ?? slider.valueSuffix,
      slider.displayValueFormat ?? slider.valueFormat,
    );
    const secondary = this.getSliderSecondaryDisplay(slider, resolvedState);

    return secondary ? `${primary} / ${secondary}` : primary;
  }

  getSliderMinLabel(slider: PlannerSliderConfig): string {
    if (slider.minLabel) {
      return slider.minLabel;
    }

    return this.formatValue(slider.min ?? 0, 0, '', slider.valueSuffix, slider.valueFormat);
  }

  getSliderMaxLabel(slider: PlannerSliderConfig): string {
    if (slider.maxLabel) {
      return slider.maxLabel;
    }

    return this.formatValue(slider.max ?? 100, 0, '', slider.valueSuffix, slider.valueFormat);
  }

  getSliderPercentPosition(slider: PlannerSliderConfig): number {
    const min = this.toFiniteNumber(slider.min, 0);
    const max = this.toFiniteNumber(slider.max, 100);
    const value = this.toFiniteNumber(this.formState[slider.key], min);
    const range = max - min;
    if (!Number.isFinite(range) || range <= 0) {
      return 0;
    }

    return this.clampNumber(((value - min) / range) * 100, 0, 100);
  }

  getSliderPercentLabel(slider: PlannerSliderConfig): string {
    const value = this.toFiniteNumber(this.formState[slider.key], 0);
    const decimals = Math.min(this.decimalPlaces(slider.step ?? 1), 2);
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
    return `${formatted}%`;
  }

  getFieldInputType(field: PlannerFieldConfig): string {
    return field.type === 'number' || field.type === 'currency' ? 'number' : 'text';
  }

  getFieldDisplayValue(field: PlannerFieldConfig): any {
    const resolvedState = this.getResolvedState();
    const rawValue = resolvedState[field.key];

    if (field.type === 'currency') {
      return this.formatValue(rawValue, field.decimals ?? 0, field.prefix || '$', field.suffix, field.valueFormat);
    }

    if (field.editable === false && field.type === 'number') {
      return this.formatValue(rawValue, field.decimals ?? 0, field.prefix, field.suffix, field.valueFormat);
    }

    return rawValue ?? '';
  }

  getEditableFieldValue(field: PlannerFieldConfig): any {
    return this.formState[field.key] ?? '';
  }

  getResultValue(row: PlannerResultRowConfig): string {
    const resolvedState = this.getResolvedState();
    const rawValue = row.template
      ? this.resolveDynamicValue(row.template)
      : row.valueKey
        ? resolvedState[row.valueKey] ?? row.value
        : row.value;

    if (typeof rawValue === 'number') {
      const prefix = row.tone === 'auto' && rawValue > 0 && !row.prefix ? '+' : row.prefix;
      return this.formatValue(rawValue, row.decimals ?? 0, prefix, row.suffix, row.valueFormat);
    }

    return String(rawValue ?? '');
  }

  getResultStyles(row: PlannerResultRowConfig): Record<string, string> {
    const resolvedState = this.getResolvedState();
    const rawValue = row.valueKey
      ? resolvedState[row.valueKey] ?? row.value
      : row.value;

    const numericValue = Number(rawValue);
    const tone = row.tone || 'neutral';
    let color = row.neutralColor || '#111827';

    if (tone === 'positive') {
      color = row.positiveColor || '#16a34a';
    } else if (tone === 'negative') {
      color = row.negativeColor || '#dc2626';
    } else if (tone === 'auto' && Number.isFinite(numericValue)) {
      if (numericValue > 0) {
        color = row.positiveColor || '#16a34a';
      } else if (numericValue < 0) {
        color = row.negativeColor || '#dc2626';
      }
    }

    return { color };
  }

  getActionButtonStyles(): Record<string, string> {
    const actionButton = this.widgetConfig?.actionButton;
    return {
      background: String(actionButton?.backgroundColor || 'var(--dvm-btn-primary-bg, #dc2626)'),
      color: String(actionButton?.textColor || 'var(--dvm-btn-primary-text, #ffffff)'),
    };
  }

  private initializeComponent(): void {
    this.dismissed = false;
    this.loading = true;
    this.errorMessage = '';
    this.submitMessage = '';
    this.submitErrorMessage = '';
    this.widgetConfig = null;
    this.resolvedSubtitle = '';
    this.sliderConfigs = [];
    this.fieldConfigs = [];
    this.resultRows = [];
    this.formState = {};
    this.configSub?.unsubscribe();
    this.dataSub?.unsubscribe();

    if (!this.data) {
      this.loading = false;
      return;
    }

    const configUrl = this.data.apiUrl || '';
    if (!configUrl) {
      this.applyConfig(this.data);
      return;
    }

    this.configSub = this.configService
      .getConfig(configUrl, this.data, undefined, this.resolveMethod(configUrl, this.data.method))
      .subscribe({
        next: (remoteConfig) => this.applyConfig(this.mergePlainObjects(remoteConfig, this.data!)),
        error: () => {
          this.loading = false;
          this.errorMessage = 'Unable to load price planner configuration.';
        },
      });
  }

  private applyConfig(config: PricePlannerConfig): void {
    this.widgetConfig = config;
    this.resolvedSubtitle = config.subtitle || '';
    this.sliderConfigs = Array.isArray(config.sliders) ? config.sliders : [];
    this.fieldConfigs = Array.isArray(config.fields) ? config.fields : [];
    this.resultRows = Array.isArray(config.resultRows) ? config.resultRows : [];
    this.formState = this.buildInitialState(config);
    this.applyResolvedTheme();
    this.loading = false;
    this.refreshPlannerData();
  }

  private buildInitialState(config: PricePlannerConfig): Record<string, any> {
    const nextState: Record<string, any> = {};

    (config.sliders || []).forEach((slider) => {
      if (!slider.key) {
        return;
      }
      const fallback = slider.defaultValue ?? slider.value ?? slider.min ?? 0;
      nextState[slider.key] = this.toFiniteNumber(slider.value ?? slider.defaultValue, Number(fallback));
      this.applySliderDynamicBounds(slider, nextState[slider.key]);
    });

    (config.fields || []).forEach((field) => {
      if (!field.key) {
        return;
      }

      if (field.type === 'number' || field.type === 'currency') {
        nextState[field.key] = this.toFiniteNumber(field.value ?? field.defaultValue, 0);
        return;
      }

      nextState[field.key] = field.value ?? field.defaultValue ?? '';
    });

    return nextState;
  }

  private getResolvedState(): Record<string, any> {
    return { ...this.formState };
  }

  private getSliderSecondaryDisplay(slider: PlannerSliderConfig, resolvedState: Record<string, any>): string {
    const secondaryRaw = slider.secondaryValueKey
      ? resolvedState[slider.secondaryValueKey]
      : slider.secondaryValue;

    if (secondaryRaw === undefined || secondaryRaw === null || secondaryRaw === '') {
      return '';
    }

    if (typeof secondaryRaw === 'number') {
      return this.formatValue(
        secondaryRaw,
        slider.secondaryDecimals ?? 0,
        slider.secondaryPrefix,
        slider.secondarySuffix,
        slider.valueFormat,
      );
    }

    return `${slider.secondaryPrefix || ''}${secondaryRaw}${slider.secondarySuffix || ''}`;
  }

  private emitFilterPayload(resolvedState: Record<string, any>, actionButton?: PlannerActionButtonConfig): void {
    const filterKey = actionButton?.filterKey || this.data?.filterKey;
    if (!filterKey) {
      return;
    }

    const listener = actionButton?.filterListener || actionButton?.filterListner || this.data?.filterListener || this.data?.filterListner;
    const payloadKeys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);
    const payload = payloadKeys.reduce<Record<string, any>>((acc, key) => {
      acc[key] = resolvedState[key];
      return acc;
    }, {});

    this.chartFilterHandler.updateFilterSelection(
      {
        ...this.data,
        filterKey,
        filterListener: listener,
        filterListner: listener,
      },
      payload,
      payload,
    );
  }

  private runNavigationAction(actionButton?: PlannerActionButtonConfig): void {
    const navigationValue = actionButton?.navigationValue || this.buttonLabel;
    executeNavigationFromConfig(this.router, this.data, {
      name: navigationValue,
      value: navigationValue,
      state: this.getResolvedState(),
    });
  }

  private applyResolvedTheme(): void {
    const overrides = this.widgetConfig?.themeOverrides || this.data?.themeOverrides;
    if (!overrides) {
      return;
    }
    const resolvedTheme = this.themeService.resolve(overrides);
    this.themeService.applyThemeTokensTo(resolvedTheme, this.hostElement.nativeElement);
  }

  private mergePlainObjects<T>(base: T | undefined, override: T | undefined): T {
    if (!base) {
      return (override || {}) as T;
    }
    if (!override) {
      return base;
    }

    const merged = { ...(base as Record<string, any>) };
    Object.keys(override as Record<string, any>).forEach((key) => {
      const overrideValue = (override as Record<string, any>)[key];
      const baseValue = (base as Record<string, any>)[key];
      if (
        overrideValue &&
        typeof overrideValue === 'object' &&
        !Array.isArray(overrideValue) &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        merged[key] = this.mergePlainObjects(baseValue, overrideValue);
      } else {
        merged[key] = overrideValue;
      }
    });
    return merged as T;
  }

  private resolveMethod(url: string, explicitMethod?: string): string {
    if (explicitMethod) {
      return explicitMethod;
    }
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  private buildRequestFilters(requestPayload: any, queryParams: any): any {
    const filters: any = {};

    if (requestPayload !== undefined && requestPayload !== null && requestPayload !== '') {
      filters.requestParams = requestPayload;
    }

    if (queryParams && typeof queryParams === 'object' && !Array.isArray(queryParams)) {
      filters.queryParams = queryParams;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  private refreshPlannerData(): void {
    const dataApiUrl = this.widgetConfig?.dataApiUrl;
    if (!dataApiUrl) {
      return;
    }

    const requestData = this.buildPlannerDataRequest();
    this.dataSub?.unsubscribe();
    this.submitting = true;
    this.dataSub = this.configService
      .getConfig(
        dataApiUrl,
        {
          ...this.widgetConfig,
          apiUrl: dataApiUrl,
          functionName: this.widgetConfig?.dataFunctionName || this.widgetConfig?.['functionName' as keyof PricePlannerConfig],
          version: this.widgetConfig?.dataVersion || this.widgetConfig?.version || '1.0',
          defaultRequestData: requestData,
        },
        { requestParams: requestData },
        this.widgetConfig?.dataMethod || this.widgetConfig?.method || 'post',
      )
      .subscribe({
        next: (response) => {
          this.submitting = false;
          this.applyPlannerDataResponse(response);
        },
        error: () => {
          this.submitting = false;
          this.submitErrorMessage = 'Unable to refresh planner data.';
        },
      });
  }

  private buildPlannerDataRequest(includeNavigationRequestParams = true, baseRequestKeys?: string[]): Record<string, any> {
    const state = this.getResolvedState();
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId || this.widgetConfig?.widgetId);
    const navigationRequestParams = includeNavigationRequestParams ? navigationFilters?.requestParams || {} : {};
    const baseRequestData: Record<string, any> = {
      ...(this.widgetConfig?.defaultRequestData || {}),
      ...navigationRequestParams,
    };
    const allowedBaseKeys = Array.isArray(baseRequestKeys) && baseRequestKeys.length
      ? baseRequestKeys.map((key) => String(key || '').trim()).filter(Boolean)
      : [];
    const requestData: Record<string, any> = allowedBaseKeys.length
      ? allowedBaseKeys.reduce<Record<string, any>>((acc, key) => {
          if (baseRequestData[key] !== undefined && baseRequestData[key] !== null && baseRequestData[key] !== '') {
            acc[key] = baseRequestData[key];
          }
          return acc;
        }, {})
      : { ...baseRequestData };

    const configuredFields = this.widgetConfig?.dataRequestFields;
    const requestFields = Array.isArray(configuredFields) && configuredFields.length
      ? configuredFields
      : [
          ...(this.sliderConfigs || []).map((slider) => slider.key),
          ...(this.fieldConfigs || []).map((field) => field.key),
        ];

    requestFields.forEach((key) => {
      if (state[key] !== undefined && state[key] !== null && state[key] !== '') {
        requestData[key] = state[key];
      }
    });

    this.sliderConfigs.forEach((slider) => {
      const sourceKey = slider.requestValueKey || slider.key;
      if (slider.requestKey && state[sourceKey] !== undefined) {
        requestData[slider.requestKey] = state[sourceKey];
        if (slider.requestKey !== slider.key) {
          delete requestData[slider.key];
        }
        if (slider.requestValueKey && slider.requestKey !== slider.requestValueKey) {
          delete requestData[slider.requestValueKey];
        }
      }
    });

    this.fieldConfigs.forEach((field) => {
      if (field.requestKey && state[field.key] !== undefined) {
        requestData[field.requestKey] = state[field.key];
        if (field.requestKey !== field.key) {
          delete requestData[field.key];
        }
      }
    });

    return Object.keys(requestData).reduce<Record<string, any>>((acc, key) => {
      acc[key] = requestData[key];
      return acc;
    }, {});
  }

  private applyPlannerDataResponse(response: any): void {
    if (!response || typeof response !== 'object') {
      return;
    }

    const mappings = this.widgetConfig?.dataResponseMappings || {};
    const responseData = this.getPrimaryResponseData(response);
    this.applyResponseSubtitle(responseData, response);

    this.sliderConfigs.forEach((slider) => {
      const responseKey = mappings.sliders?.[slider.key] || slider.key;
      const value = this.getMappedResponseValue(responseData, response, responseKey);
      if (value !== undefined && value !== null && value !== '') {
        const numeric = this.toFiniteNumber(value, this.formState[slider.key]);
        const nextValue = slider.absoluteValue === true ? Math.abs(numeric) : numeric;
        this.formState[slider.key] = this.clampNumber(
          nextValue,
          this.toFiniteNumber(slider.min, Number.NEGATIVE_INFINITY),
          this.toFiniteNumber(slider.max, Number.POSITIVE_INFINITY),
        );
        this.applySliderDynamicBounds(slider, numeric);
        this.updateDerivedSliderValue(slider);
      }
    });

    this.fieldConfigs.forEach((field) => {
      const responseKey = mappings.fields?.[field.key] || field.key;
      const value = this.getMappedResponseValue(responseData, response, responseKey);
      if (value !== undefined && value !== null) {
        this.formState[field.key] = field.type === 'number' || field.type === 'currency'
          ? this.toFiniteNumber(value, this.formState[field.key])
          : value;
      }
    });

    Object.entries(mappings.fields || {}).forEach(([stateKey, responseKey]) => {
      if (!stateKey || this.fieldConfigs.some((field) => field.key === stateKey)) {
        return;
      }

      const value = this.getMappedResponseValue(responseData, response, String(responseKey));
      if (value !== undefined && value !== null) {
        this.formState[stateKey] = value;
      }
    });

    this.resultRows = this.resultRows.map((row) => {
      const responseKey = row.valueKey
        ? mappings.resultRows?.[row.valueKey] || row.valueKey
        : mappings.resultRows?.[row.label];
      const value = responseKey ? this.getMappedResponseValue(responseData, response, responseKey) : undefined;
      if (value !== undefined && value !== null && row.valueKey) {
        this.formState[row.valueKey] = value;
      }
      return value === undefined || value === null
        ? row
        : {
            ...row,
            value,
      };
    });
  }

  private getPrimaryResponseData(response: any): any {
    const wrappedData = response?.responseList?.[0]?.data;
    if (Array.isArray(wrappedData)) {
      return wrappedData[0] || {};
    }
    return wrappedData && typeof wrappedData === 'object' ? wrappedData : response;
  }

  private applyResponseSubtitle(responseData: any, response: any): void {
    const template = this.widgetConfig?.subtitleTemplate || this.widgetConfig?.subtitle;
    if (!template || !this.widgetConfig) {
      return;
    }

    const resolved = String(template).replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const trimmedToken = token.trim();
      const value = this.getMappedResponseValue(responseData, response, trimmedToken);
      return value === undefined || value === null ? '' : String(value);
    });

    this.resolvedSubtitle = resolved
      .replace(/\s+-\s+$/g, '')
      .replace(/^\s+-\s+/g, '')
      .trim();
  }

  private getMappedResponseValue(primaryData: any, response: any, path: string): any {
    const primaryValue = this.getValueByPath(primaryData, path);
    return primaryValue !== undefined ? primaryValue : this.getValueByPath(response, path);
  }

  private applySliderDynamicBounds(slider: PlannerSliderConfig, value: number): void {
    if (!Number.isFinite(value)) {
      return;
    }

    const minMultiplier = Number(slider.minMultiplier);
    const maxMultiplier = Number(slider.maxMultiplier);
    if (Number.isFinite(minMultiplier) && minMultiplier > 0) {
      slider.min = Math.floor(value / minMultiplier);
    }
    if (Number.isFinite(maxMultiplier) && maxMultiplier > 0) {
      slider.max = Math.ceil(value * maxMultiplier);
    }
  }

  private updateDerivedSliderValue(slider: PlannerSliderConfig): void {
    const derived = slider.derivedValue;
    const targetKey = String(derived?.targetKey || '').trim();
    if (!derived || !targetKey) {
      return;
    }

    const baseRaw = derived.baseValueKey
      ? this.formState[derived.baseValueKey]
      : undefined;
    const divisorRaw = derived.divisorKey
      ? this.formState[derived.divisorKey]
      : undefined;
    const baseValue = this.toFiniteNumber(baseRaw, Number.NaN);
    const divisorValue = this.toFiniteNumber(divisorRaw, 1);
    const percentage = Math.abs(this.toFiniteNumber(this.formState[slider.key], 0));

    if (!Number.isFinite(baseValue) || !Number.isFinite(divisorValue) || divisorValue === 0) {
      return;
    }

    const unitBase = baseValue / divisorValue;
    const multiplier = (derived.mode || 'decrease-by-percent') === 'increase-by-percent'
      ? 1 + (percentage / 100)
      : 1 - (percentage / 100);
    const nextValue = unitBase * multiplier;
    if (Number.isFinite(nextValue)) {
      this.formState[targetKey] = Math.round(nextValue);
    }
  }

  private resolveDynamicValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.resolveDynamicValue(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: Record<string, any>, key: string) => {
        acc[key] = this.resolveDynamicValue(value[key]);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    const exactMatch = value.match(/^\{\{\s*([^}]+)\s*\}\}$/);
    if (exactMatch) {
      return this.resolveDynamicToken(exactMatch[1]);
    }

    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, token: string) => {
      const resolved = this.resolveDynamicToken(token);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }

  private resolveDynamicToken(token: string): any {
    const normalized = token.trim();
    const state = this.getResolvedState();

    if (normalized === 'state') {
      return state;
    }
    if (normalized.startsWith('state.')) {
      return this.getValueByPath(state, normalized.slice(6));
    }
    if (normalized === 'widget') {
      return this.widgetConfig;
    }
    if (normalized.startsWith('widget.')) {
      return this.getValueByPath(this.widgetConfig, normalized.slice(7));
    }
    if (normalized === 'filters') {
      return this.globalFilterService.getCurrentFilters();
    }
    if (normalized.startsWith('filters.')) {
      return this.getValueByPath(this.globalFilterService.getCurrentFilters(), normalized.slice(8));
    }

    return this.getValueByPath(state, normalized);
  }

  private formatValue(
    rawValue: unknown,
    decimals = 0,
    prefix = '',
    suffix = '',
    valueFormat?: PlannerValueFormat,
  ): string {
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue)) {
      if (valueFormat === 'compact') {
        return `${prefix || ''}${this.formatCompactValue(numericValue, decimals)}${suffix || ''}`;
      }
      return `${prefix || ''}${numericValue.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix || ''}`;
    }

    if (rawValue === undefined || rawValue === null) {
      return '';
    }

    return `${prefix || ''}${String(rawValue)}${suffix || ''}`;
  }

  private formatCompactValue(value: number, decimals: number): string {
    const absValue = Math.abs(value);
    const units = [
      { threshold: 1_000_000_000, suffix: 'B' },
      { threshold: 1_000_000, suffix: 'M' },
      { threshold: 1_000, suffix: 'k' },
    ];
    const unit = units.find((entry) => absValue >= entry.threshold);
    if (!unit) {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    const scaled = value / unit.threshold;
    const resolvedDecimals = decimals > 0
      ? decimals
      : Math.abs(scaled) < 10 && !Number.isInteger(scaled)
        ? 1
        : 0;
    return `${scaled.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: resolvedDecimals,
    })}${unit.suffix}`;
  }

  private toFiniteNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private decimalPlaces(value: number): number {
    const text = String(value ?? 1);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private getValueByPath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }
}
